<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import { ParticipationState } from '@/store/consultation.interface';
import { Locales } from '@/store/language.interface';
import { useGuidedConsultationProgress } from '@/composables/useGuidedConsultationProgress';

const props = defineProps<{
  participationState: ParticipationState;
  languageEnabled: Locales[];
}>();

const emit = defineEmits<{
  (e: 'navigateToForm', formId: number, sectionOrderNum: number): void;
  (e: 'submit'): void;
}>();

const {
  isCompleted,
  canComplete,
  mandatoryTotal,
  mandatoryAnswered,
  totalVisible,
  totalAnswered,
  sectionProgress,
  missingForms,
  labels,
} = useGuidedConsultationProgress(toRef(props, 'participationState'), toRef(props, 'languageEnabled'));

const isExpanded = ref(false);
const isSubmitConfirmationOpen = ref(false);

const hasUnansweredOptional = computed(() => canComplete.value && totalAnswered.value < totalVisible.value);

const primaryButtonText = computed(() => (canComplete.value ? labels.value.actionText : labels.value.completeText));

const primaryButtonIcon = computed(() => (canComplete.value ? 'send' : 'lock'));

const statusSecondaryText = computed(() => {
  if (!canComplete.value && !isCompleted.value) {
    return '';
  }

  return labels.value.secondaryText;
});

function navigateToQuestion(formId: number, sectionOrderNum: number) {
  isExpanded.value = false;
  emit('navigateToForm', formId, sectionOrderNum);
}

function handleAction() {
  if (!canComplete.value) {
    isExpanded.value = !isExpanded.value;
    return;
  }

  isExpanded.value = false;
  if (hasUnansweredOptional.value) {
    isSubmitConfirmationOpen.value = true;
    return;
  }

  emit('submit');
}

function closeSubmitConfirmation() {
  isSubmitConfirmationOpen.value = false;
}

function submitWithMissingOptional() {
  isSubmitConfirmationOpen.value = false;
  emit('submit');
}
</script>

<template>
  <div class="qpb-desktop">
    <!-- Slide-up detail panel -->
    <Transition name="qpb-slide">
      <div v-if="isExpanded && !isCompleted" class="qpb-desktop__panel">
        <div class="qpb-desktop__panel-inner">
          <div class="qpb-desktop__panel-header">
            <span class="qpb-desktop__panel-title">{{ labels.panelTitle }}</span>
          </div>

          <div v-if="missingForms.length > 0" class="qpb-desktop__chip-list">
            <button
              v-for="form in missingForms"
              :key="form.id"
              class="qpb-desktop__chip"
              @click="navigateToQuestion(form.id, form.sectionOrderNum)"
            >
              <span class="material-icons qpb-desktop__chip-icon">north_east</span>
              <span class="qpb-desktop__chip-title">{{ form.title }}</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Fixed bottom bar -->
    <div class="qpb-desktop__bar-shell">
      <div class="qpb-desktop__bar">
        <div class="qpb-desktop__bar-left">
          <div class="qpb-desktop__circle" :class="{ 'qpb-desktop__circle--complete': canComplete || isCompleted }">
            <span>{{ mandatoryAnswered }}/{{ mandatoryTotal }}</span>
          </div>
          <div class="qpb-desktop__status">
            <div
              class="qpb-desktop__status-primary"
              :class="{ 'qpb-desktop__status-primary--subtle': !canComplete && !isCompleted }"
            >
              {{ labels.primaryText }}
            </div>
            <div v-if="statusSecondaryText" class="qpb-desktop__status-secondary">{{ statusSecondaryText }}</div>
          </div>
        </div>

        <div v-if="!isCompleted" class="qpb-desktop__bar-center">
          <div class="qpb-desktop__bar-center-header">
            <span class="qpb-desktop__bar-center-label">{{ labels.auTotal }}</span>
            <span class="qpb-desktop__bar-center-count">{{ totalAnswered }}/{{ totalVisible }}</span>
          </div>
          <div class="qpb-desktop__section-progress">
            <div
              v-for="section in sectionProgress"
              :key="section.sectionOrderNum"
              class="qpb-desktop__section-track"
              :title="`${section.answeredCount}/${section.visibleCount}`"
            >
              <div class="qpb-desktop__section-fill" :style="{ width: section.pct + '%' }" />
            </div>
          </div>
        </div>

        <div v-if="!isCompleted" class="qpb-desktop__bar-right">
          <button class="qpb-btn qpb-btn--primary" @click="handleAction">
            {{ primaryButtonText }}
            <span class="material-icons">{{ primaryButtonIcon }}</span>
          </button>
        </div>
      </div>
    </div>

    <q-dialog v-model="isSubmitConfirmationOpen">
      <q-card class="qpb-desktop__submit-dialog">
        <q-card-section>
          <div class="qpb-desktop__submit-dialog-title">
            {{ labels.submitConfirmationTitle }}
          </div>
          <div class="qpb-desktop__submit-dialog-text">
            {{
              labels.submitConfirmationBodyStart +
              ' ' +
              labels.submitConfirmationBodyCount +
              ' ' +
              labels.submitConfirmationBodyEnd
            }}
          </div>
        </q-card-section>
        <q-card-actions align="right" class="qpb-desktop__submit-dialog-actions">
          <button class="qpb-btn qpb-btn--ghost" @click="closeSubmitConfirmation">
            {{ labels.submitConfirmationCancel }}
          </button>
          <button class="qpb-btn qpb-btn--primary" @click="submitWithMissingOptional">
            {{ labels.submitConfirmationConfirm }}
            <span class="material-icons">send</span>
          </button>
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<style lang="scss">
.qpb-desktop {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 200;
  display: flex;
  flex-direction: column;

  &__panel {
    background: $color-neutral-white;
    border-top: 1px solid $color-neutral-grey-300;
    padding: $space-md $space-lg;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.1);
    max-height: 50vh;
    overflow-y: auto;
  }

  &__panel-inner {
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
  }

  &__panel-header {
    display: flex;
    align-items: center;
    gap: $space-sm;
    margin-bottom: $space-sm;
  }

  &__panel-title {
    font-size: 15px;
    font-weight: 700;
    color: $color-neutral-black;
  }

  &__chip-list {
    display: flex;
    flex-wrap: wrap;
    gap: $space-sm;
    margin-top: $space-xs;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: $color-orange-12;
    border: 1px solid $color-neutral-grey-300;
    border-radius: 8px;
    padding: 8px 14px;
    cursor: pointer;
    font-size: 13px;
    color: $color-neutral-black;
    transition:
      background 0.15s ease,
      border-color 0.15s ease,
      color 0.15s ease;

    &:hover {
      background: $color-orange-12;
      border-color: $color-orange-50;

      .qpb-desktop__chip-icon {
        color: $color-orange;
      }
    }
  }

  &__chip-icon {
    font-size: 16px;
    color: $color-neutral-grey-400-50;
    transition: color 0.15s ease;
  }

  &__chip-title {
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__bar-shell {
    background: $color-neutral-white;
    border-top: 1px solid $color-neutral-grey-300;
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
    width: 100%;
    display: flex;
    justify-content: center;
  }

  &__bar {
    display: flex;
    align-items: center;
    gap: $space-md;
    padding: $space-sm $space-lg;
    min-height: 72px;
    width: 100%;
    max-width: 1100px;
    box-sizing: border-box;
  }

  &__bar-left {
    display: flex;
    align-items: center;
    gap: $space-md;
    flex-shrink: 0;
  }

  &__bar-center {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: $space-xs;
    padding: 0 $space-md;
  }

  &__bar-center-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: $space-sm;
  }

  &__bar-center-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: $color-neutral-grey-400-50;
    text-transform: uppercase;
  }

  &__bar-center-count {
    font-size: 11px;
    font-weight: 600;
    color: $color-neutral-black;
    white-space: nowrap;
  }

  &__section-progress {
    display: flex;
    gap: 6px;
    width: 100%;
  }

  &__section-track {
    flex: 1;
    height: 6px;
    background: $color-neutral-grey-300;
    border-radius: 999px;
    overflow: hidden;
    min-width: 40px;
  }

  &__section-fill {
    height: 100%;
    background: $color-primary;
    border-radius: 999px;
    transition: width 0.4s ease;
    max-width: 100%;
  }

  &__bar-right {
    display: flex;
    align-items: center;
    gap: $space-sm;
    flex-shrink: 0;
  }

  &__circle {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 2px solid $color-neutral-grey-300;
    background: $color-neutral-white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 700;
    color: $color-neutral-black;

    &--complete {
      border-color: $color-primary;
      background: $color-primary-12;
      color: $color-primary-darken;
    }
  }

  &__status {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__status-primary {
    font-size: 14px;
    font-weight: 600;
    color: $color-neutral-black;
    white-space: nowrap;

    &--subtle {
      font-size: 12px;
      font-weight: 500;
      color: $color-neutral-grey-400;
      letter-spacing: 0.03em;
    }
  }

  &__status-secondary {
    font-size: 12px;
    color: $color-neutral-grey-400-50;
    white-space: nowrap;
  }

  &__submit-dialog {
    border-radius: 24px;
    max-width: 560px;
    width: calc(100vw - #{$space-md * 2});
    padding: $space-md;
  }

  &__submit-dialog-title {
    font-size: 30px;
    line-height: 1.1;
    font-weight: 700;
    color: $color-neutral-black;
    margin-bottom: $space-sm;
  }

  &__submit-dialog-text {
    font-size: 20px;
    line-height: 1.25;
    color: $color-neutral-black;
    word-break: break-word;
  }

  &__submit-dialog-actions {
    padding: 0 $space-md $space-md;
    gap: $space-sm;
  }
}

// ── Shared progress row (used in both desktop and mobile) ──────────────────
.qpb-progress-row {
  display: flex;
  align-items: center;
  gap: $space-sm;

  &__label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: $color-neutral-grey-400-50;
    text-transform: uppercase;
    width: 90px;
    flex-shrink: 0;
  }

  &__track {
    flex: 1;
    height: 6px;
    background: $color-neutral-grey-300;
    border-radius: 3px;
    overflow: hidden;
    min-width: 80px;
  }

  &__fill {
    height: 100%;
    background: $color-primary;
    border-radius: 3px;
    transition: width 0.4s ease;
    max-width: 100%;
  }

  &__count {
    font-size: 11px;
    font-weight: 600;
    color: $color-neutral-black;
    width: 36px;
    text-align: right;
    flex-shrink: 0;
  }
}

// ── Shared action buttons ──────────────────────────────────────────────────
.qpb-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    background 0.15s ease,
    opacity 0.15s ease;

  .material-icons {
    font-size: 18px;
  }

  &--primary {
    background: $color-primary;
    color: $color-neutral-white;

    &:hover {
      opacity: 0.9;
    }
  }

  &--ghost {
    background: transparent;
    color: $color-primary;
    border: 1px solid $color-primary;

    &:hover {
      background: $color-primary-12;
    }
  }

  &--full {
    width: 100%;
    justify-content: center;
  }
}

// ── Desktop slide transition ───────────────────────────────────────────────
.qpb-slide-enter-active,
.qpb-slide-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.2s ease;
}

.qpb-slide-enter-from,
.qpb-slide-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
